import axios, { AxiosInstance } from 'axios';
import getConfig from 'next/config';

import customerDetails from './customerDetails';
import {
  CodeCredentials,
  CustomerCredentials,
  isCodeCredentials,
  isLegacyCredentials,
  ShopTypeEnum,
  SspConfiguration,
  FlowSubmitUrl,
  IOrder,
  IFulfillment,
  ReportIssueReasonEnum,
} from './types';
import {
  GetShopifyOrderPayload,
  RequestFlowParams,
  SendCodeParams,
  VerifyCodeParams,
} from './types/dto';
import { AutomatedResponseEventEnum } from './types/enums/AutomatedResponseEvent.enum';
import isValidShopName from './utils/isValidShopName';
import removeEmptyValues from './utils/removeEmptyValues';

type GetShopifyOrdersParams = CustomerCredentials & {
  shopName: string;
};

export default class SelfServicePortalApi {
  private static instance: SelfServicePortalApi | null = null;

  public static getInstance(): SelfServicePortalApi {
    SelfServicePortalApi.instance =
      SelfServicePortalApi.instance || new SelfServicePortalApi();
    return SelfServicePortalApi.instance;
  }

  private axiosInstance: AxiosInstance;

  private cdnInstance: AxiosInstance;

  static shopType(): ShopTypeEnum {
    return ShopTypeEnum.SHOPIFY;
  }

  constructor() {
    const { publicRuntimeConfig } = getConfig();
    // For development we don't have a CDN, we're using the same ssp-api base url
    const chatCdnUrl =
      publicRuntimeConfig.environment === 'development'
        ? publicRuntimeConfig.chatSelfServicePortalApiUrl
        : publicRuntimeConfig.chatCdnBaseUrl;

    this.axiosInstance = axios.create({
      baseURL: publicRuntimeConfig.chatSelfServicePortalApiUrl,
    });
    this.cdnInstance = axios.create({
      baseURL: chatCdnUrl,
    });
  }

  async getConfiguration(shopName: string): Promise<SspConfiguration> {
    const shopType = SelfServicePortalApi.shopType();

    const response = await this.cdnInstance.get(
      `/ssp/configurations?shop_name=${shopName}&type=${shopType}`,
    );

    if (!response || response.statusText !== 'OK') {
      throw new Error('An error occurred while retrieving the configuration');
    }

    return response.data;
  }

  async sendCode(params: SendCodeParams) {
    return this.axiosInstance.post<void>(
      `/ssp/login/send-code`,
      {},
      {
        params,
      },
    );
  }

  async verifyCode(params: VerifyCodeParams) {
    return this.axiosInstance.post<CodeCredentials>(
      `/ssp/login/verify-code`,
      {},
      {
        params,
      },
    );
  }

  async getShopifyOrders(params: GetShopifyOrdersParams) {
    const { shopName, ...customer } = params;

    if (!isCodeCredentials(customer) && !isLegacyCredentials(customer)) {
      throw Error('Missing customer credentials');
    }

    if (!isValidShopName(shopName)) {
      throw Error('Invalid shop name');
    }

    return this.axiosInstance.get<GetShopifyOrderPayload>(
      `/ssp/shopify/orders`,
      {
        params: removeEmptyValues(params),
      },
    );
  }

  async requestSubmitFlow(flow: FlowSubmitUrl, payload: RequestFlowParams) {
    return this.axiosInstance.post<void>(`/ssp/flows/${flow}`, {
      ...removeEmptyValues(payload),
    });
  }

  async generateLoopReturnsDeepLink(
    shopName: string,
    order: IOrder,
    customerEmail: string,
  ) {
    const response = await this.axiosInstance.post<{ url: string }>(
      `/ssp/flows/loop-returns/generate-deep-link`,
      {
        channel: 'help-center',
        shopName,
        customerEmail,
        order: {
          id: order.id,
          name: order.name,
          orderNumber: order.order_number,
        },
        shippingAddressZip: order.shipping_address?.zip,
      },
    );

    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        `An error occurred calling the SSP API deep link endpoint ${response.statusText}`,
      );
    }

    if (typeof response?.data?.url == 'string') {
      return response.data.url;
    } else {
      throw new Error(
        `Unexpected SSP API deep link response ${JSON.stringify(
          response.data,
        )}`,
      );
    }
  }

  async sendAutomatedResponseEvent(
    eventType: AutomatedResponseEventEnum,
    order: IOrder,
    fulfillment: IFulfillment,
    automatedResponse: { text: string; html: string },
    reportIssueReason?: ReportIssueReasonEnum,
  ) {
    const customerId = Number(customerDetails.getCustomerId());
    const customerEmail = customerDetails.getCustomerEmail();

    try {
      const response = await this.axiosInstance.post(
        `/ssp/flows/automated-response/event`,
        {
          eventType,
          customerId,
          customerEmail,
          orderId: order.id,
          orderName: order.name,
          fulfillmentId: fulfillment.id,
          automatedResponse,
          reportIssueReason,
        },
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          `Automated response event creation failed with code ${error.response?.status}`,
        );
      }
    }
  }
}
